import { Components, Theme, CSSObject } from '@mui/material';

const outlinedBorderWidths = {
  small: 1,
  medium: 1.5,
  large: 2,
};

const buildButtonSize = (height: number, fontSize: number, lineHeight: number): CSSObject => ({
  height: `${height}px`,
  fontSize: `${fontSize}px`,
  letterSpacing: '2px',
  fontWeight: 500,
  textTransform: 'uppercase',
  lineHeight: `${lineHeight}px`,
});

export const buildMuiButtonComponent = (): Components<Omit<Theme, 'components'>>['MuiButton'] => ({
  styleOverrides: {
    root: {
      borderRadius: '4px',
    },
    sizeLarge: buildButtonSize(64, 20, 24),
    sizeMedium: buildButtonSize(44, 16, 20),
    sizeSmall: buildButtonSize(31, 12, 15),
    outlined: ({ theme, ownerState: { color = 'primary', size = 'medium' } }) => ({
      borderColor: theme.palette[color].main,
      color: theme.palette[color].main,
      backgroundColor: 'transparent',
      borderWidth: `${outlinedBorderWidths[size]}px`,
      borderStyle: 'solid',
      '&:hover': {
        backgroundColor: color === 'secondary' ? theme.palette.secondary[10] : theme.palette.primary[10],
        borderColor: theme.palette[color].dark,
        borderWidth: `${outlinedBorderWidths[size]}px`,
        color: theme.palette[color].dark,
      },
      '&.Mui-disabled': {
        color: theme.palette.primary[50],
        borderColor: theme.palette.primary[50],
        borderWidth: `${outlinedBorderWidths[size]}px`,
      },
    }),
    contained: ({ theme }) => ({
      boxShadow: '0px 5px 10px rgba(98, 110, 119, 0.34)',
      '&:hover': {
        boxShadow: '0px 5px 10px rgba(45, 59, 70, 0.34)',
      },
      '&.Mui-disabled': {
        backgroundColor: theme.palette.primary[20],
        color: theme.palette.primary[50],
      },
    }),
    containedPrimary: {
      boxShadow: '0px 5px 10px rgba(7, 23, 36, 0.34)',
      '&:hover': {
        boxShadow: '0px 5px 10px rgba(7, 23, 36, 0.34)',
      },
    },
    containedSecondary: {
      boxShadow: '0px 5px 10px rgba(109, 186, 232, 0.34)',
      '&:hover': {
        boxShadow: '0px 5px 10px rgba(62, 154, 209, 0.34)',
      },
    },
    containedError: {
      boxShadow: '0px 5px 10px rgba(244, 67, 54, 0.34)',
      '&:hover': {
        boxShadow: '0px 5px 10px rgba(157, 22, 12, 0.34)',
      },
    },
    text: ({ theme, ownerState: { color = 'primary' } }) => ({
      '&:hover': {
        backgroundColor: color === 'secondary' ? theme.palette.secondary[10] : theme.palette.primary[10],
        color: theme.palette[color].dark,
      },
      '&.Mui-disabled': {
        color: theme.palette.primary[50],
      },
    }),
  },
});
